import type { AccountSortDirection, AccountSortKey } from '../utils/accountHierarchy';

const STORAGE_KEY = 'bth_crm_accounts_list_state';

export type AccountsListPersistedState = {
  search: string;
  sortKey: AccountSortKey;
  sortDir: AccountSortDirection;
  expandedBrandParents: string[];
  scrollTop: number;
  /** Account code last opened — optional focus after restore. */
  lastOpenedCode?: string | null;
};

const SORT_KEYS: AccountSortKey[] = ['type', 'account', 'district', 'salesperson'];

function isSortKey(value: unknown): value is AccountSortKey {
  return typeof value === 'string' && (SORT_KEYS as string[]).includes(value);
}

export function readAccountsListState(): AccountsListPersistedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AccountsListPersistedState>;
    return {
      search: typeof parsed.search === 'string' ? parsed.search : '',
      sortKey: isSortKey(parsed.sortKey) ? parsed.sortKey : 'account',
      sortDir: parsed.sortDir === 'desc' ? 'desc' : 'asc',
      expandedBrandParents: Array.isArray(parsed.expandedBrandParents)
        ? parsed.expandedBrandParents.filter((id): id is string => typeof id === 'string')
        : [],
      scrollTop: typeof parsed.scrollTop === 'number' && Number.isFinite(parsed.scrollTop)
        ? Math.max(0, parsed.scrollTop)
        : 0,
      lastOpenedCode:
        typeof parsed.lastOpenedCode === 'string' && parsed.lastOpenedCode.trim()
          ? parsed.lastOpenedCode.trim()
          : null,
    };
  } catch {
    return null;
  }
}

export function writeAccountsListState(state: AccountsListPersistedState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

/** CRM accounts list route (not account detail). */
export function isAccountsListPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/accounts') return true;
  return /^\/(?:crm|it)\/accounts$/.test(normalized);
}

export function accountsListHasPendingScrollRestore(): boolean {
  const state = readAccountsListState();
  return Boolean(state && state.scrollTop > 0);
}

/** Prefer the CRM shell scroll root (dock layouts scroll `.main`, not `.page`). */
export function getAccountsPageScroller(): Element | null {
  if (document.documentElement.classList.contains('has-dock')) {
    const main = document.querySelector('.app-shell--dock .main');
    if (main) return main;
  }
  return (
    document.querySelector('.app-shell--dock .main') ??
    document.querySelector('.page') ??
    document.scrollingElement
  );
}

export function readAccountsScrollTop(): number {
  const el = getAccountsPageScroller();
  return el instanceof HTMLElement || el instanceof Element ? el.scrollTop : window.scrollY;
}

export function writeAccountsScrollTop(scrollTop: number): void {
  const el = getAccountsPageScroller();
  if (el) {
    el.scrollTop = scrollTop;
  } else {
    window.scrollTo(0, scrollTop);
  }
}
